import React, { useState } from 'react';
import { Box, ButtonBase, Menu, MenuItem, ListItemText, Tooltip } from '@mui/material';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n';
import {
  BRAND_GRADIENT,
  RULE_GRADIENT,
  EDGE,
  HOVER_BG,
  CARD_SHADOW,
  keyframe,
  panelRule,
  reduceMotion,
  G1,
} from '../theme/oceanTokens';

/** The gradient pill shown in the app bar, with a slow drifting gradient. */
const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.6,
  height: 30,
  px: 1.25,
  borderRadius: 99,
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.72rem',
  letterSpacing: '0.06em',
  background: BRAND_GRADIENT,
  backgroundSize: '240% 100%',
  boxShadow: '0 4px 12px rgb(var(--ocean-accent-rgb) / 0.26)',
  animation: `${keyframe.hue} 9s ease-in-out infinite`,
  [reduceMotion]: { animation: 'none' },
};

/**
 * Language picker for the app bar. Persists the choice via i18next's
 * localStorage detector ('app_language').
 *
 * With a single language on offer there is nothing to choose, so it renders as
 * a static badge instead of a dropdown that opens onto one item. Add a second
 * entry to SUPPORTED_LANGUAGES and it becomes a menu again on its own.
 */
const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const current =
    SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ||
    SUPPORTED_LANGUAGES[0];
  const canChoose = SUPPORTED_LANGUAGES.length > 1;

  const choose = (code) => {
    i18n.changeLanguage(code);
    setAnchorEl(null);
  };

  const label = t('common.language', 'Language');

  const face = (
    <>
      <TranslateRoundedIcon
        className="lang-glyph"
        sx={{
          fontSize: 17,
          transition: 'transform .4s cubic-bezier(.2,.8,.3,1)',
          [reduceMotion]: { transition: 'none' },
        }}
      />
      <Box component="span">{current.code.toUpperCase()}</Box>
    </>
  );

  if (!canChoose) {
    return (
      <Tooltip title={`${label}: ${current.label}`}>
        <Box sx={pillSx} aria-label={`${label}: ${current.label}`}>
          {face}
        </Box>
      </Tooltip>
    );
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={label}>
        <ButtonBase
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="listbox"
          aria-expanded={open}
          sx={{
            ...pillSx,
            transition: 'transform .2s, box-shadow .25s, background-position .55s',
            '&:hover, &.open': {
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 20px rgb(var(--ocean-accent-rgb) / 0.34)',
              backgroundPosition: '100% 0',
            },
            // The glyph turns over as the menu opens.
            '&:hover .lang-glyph, &.open .lang-glyph': { transform: 'rotate(180deg)' },
            '&:focus-visible': { outline: '2px solid #0C4A6E', outlineOffset: 2 },
            [reduceMotion]: { transition: 'none', animation: 'none' },
          }}
          className={open ? 'open' : undefined}
        >
          {face}
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        transitionDuration={220}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 190,
              borderRadius: '14px',
              overflow: 'hidden',
              // No `position` here: the Popover paper is already absolutely
              // positioned, so overriding it would drop the menu into normal
              // flow and stretch it to the full page width.
              border: `1px solid ${EDGE}`,
              boxShadow: CARD_SHADOW,
              // Same animated gradient rule the dashboard panels carry.
              '&::before': panelRule,
            },
          },
          list: { sx: { py: 0.75 } },
        }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => {
          const selected = current.code === lang.code;
          return (
            <MenuItem
              key={lang.code}
              selected={selected}
              onClick={() => choose(lang.code)}
              sx={{
                position: 'relative',
                py: 1,
                mx: 0.75,
                borderRadius: '9px',
                transition: 'background-color .2s, padding-left .2s',
                // Gradient rail slides in from the left edge.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 6,
                  bottom: 6,
                  width: 3,
                  borderRadius: 99,
                  background: RULE_GRADIENT,
                  transform: 'scaleY(0)',
                  transition: 'transform .22s',
                },
                '&:hover': { backgroundColor: HOVER_BG, pl: 2.2 },
                '&:hover::before, &.Mui-selected::before': { transform: 'scaleY(1)' },
                '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: HOVER_BG },
                [reduceMotion]: {
                  transition: 'none',
                  '&::before': { transition: 'none' },
                },
              }}
            >
              <ListItemText
                primary={lang.native}
                secondary={lang.label}
                primaryTypographyProps={{ fontWeight: selected ? 700 : 500, fontSize: '0.88rem' }}
                secondaryTypographyProps={{ fontSize: '0.72rem' }}
              />
              {selected && (
                <CheckRoundedIcon
                  sx={{
                    fontSize: 18,
                    ml: 1,
                    color: G1,
                    animation: `${keyframe.rise} .3s ease backwards`,
                    [reduceMotion]: { animation: 'none' },
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
